import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, Trophy, Target, Flame, Award } from 'lucide-react';

const StatsDashboard = ({ userId, className = '' }) => {
  const { API } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      fetchStats();
    }
  }, [userId]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}/stats-dashboard`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>סטטיסטיקות</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-gray-500">טוען...</div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) {
    return null;
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="w-5 h-5" />
          סטטיסטיקות מפורטות
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {/* נקודות */}
          <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
            <TrendingUp className="w-6 h-6 mx-auto mb-2 text-purple-600" />
            <div className="text-2xl font-bold text-purple-700">{stats.points}</div>
            <div className="text-xs text-gray-600">נקודות</div>
            {stats.points_change_week !== 0 && (
              <div className={`text-xs mt-1 ${stats.points_change_week > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {stats.points_change_week > 0 ? '↑' : '↓'} {Math.abs(stats.points_change_week)} השבוע
              </div>
            )}
          </div>

          {/* ניצחונות */}
          <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg">
            <Trophy className="w-6 h-6 mx-auto mb-2 text-green-600" />
            <div className="text-2xl font-bold text-green-700">{stats.total_wins}</div>
            <div className="text-xs text-gray-600">ניצחונות</div>
          </div>

          {/* הפסדים */}
          <div className="text-center p-4 bg-gradient-to-br from-red-50 to-red-100 rounded-lg">
            <div className="text-2xl font-bold text-red-700">{stats.total_losses}</div>
            <div className="text-xs text-gray-600">הפסדים</div>
          </div>

          {/* Win Rate */}
          <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
            <Target className="w-6 h-6 mx-auto mb-2 text-blue-600" />
            <div className="text-2xl font-bold text-blue-700">{stats.win_rate}%</div>
            <div className="text-xs text-gray-600">אחוז ניצחונות</div>
          </div>

          {/* Streak נוכחי */}
          <div className="text-center p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg">
            <Flame className="w-6 h-6 mx-auto mb-2 text-orange-600" />
            <div className="text-2xl font-bold text-orange-700">
              {stats.current_streak.count}{stats.current_streak.type}
            </div>
            <div className="text-xs text-gray-600">רצף נוכחי</div>
          </div>

          {/* Best Streak */}
          <div className="text-center p-4 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg">
            <Award className="w-6 h-6 mx-auto mb-2 text-yellow-600" />
            <div className="text-2xl font-bold text-yellow-700">{stats.best_win_streak}W</div>
            <div className="text-xs text-gray-600">רצף שיא</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StatsDashboard;
